import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/**
 * Analytics + Google Search Console verification injector.
 *
 * ACTIVATION:
 *   1. Set REACT_APP_GA4_ID (format: G-XXXXXXXXXX) in .env.production.
 *   2. Set REACT_APP_GSC_VERIFICATION (google-site-verification=...) if
 *      using HTML-meta verification instead of DNS-TXT.
 *   3. Set REACT_APP_CF_BEACON (Cloudflare Web Analytics token) if enabled.
 *
 * When ANY env var above is missing / empty, that integration is skipped
 * entirely — nothing loads in the browser (privacy-safe by default).
 *
 * Dev mode: never loads GA4 unless REACT_APP_ENV === "production".
 */
export default function Analytics() {
  const ga4Id  = process.env.REACT_APP_GA4_ID?.trim();
  const gscTag = process.env.REACT_APP_GSC_VERIFICATION?.trim();
  const cfBeacon = process.env.REACT_APP_CF_BEACON?.trim();
  const env    = process.env.REACT_APP_ENV || "development";
  const isProd = env === "production";

  // GA4 bootstrap — imperative, because inline <script> injected via
  // react-helmet is NOT executed by browsers (innerHTML scripts don't run).
  useEffect(() => {
    if (!isProd || !ga4Id || typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
    }
    window.gtag("js", new Date());
    // send_page_view:false — page views are sent manually below (SPA).
    window.gtag("config", ga4Id, { send_page_view: false, anonymize_ip: true });
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
      document.head.appendChild(s);
    }
  }, [ga4Id, isProd]);

  // Manual SPA pageview tracking — gtag config with send_page_view=false
  // then fire on every route change.
  useEffect(() => {
    if (!isProd || !ga4Id || typeof window === "undefined") return;
    const send = () => {
      const path = window.location.pathname + window.location.search;
      // Dedupe guard: never send the same path twice in a row
      // (protects against StrictMode double-effects and pushState echoes).
      if (window.__ga4LastPath === path) return;
      window.__ga4LastPath = path;
      if (window.gtag) {
        window.gtag("event", "page_view", {
          page_path: path,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    };
    // Fire on first mount + every history push.
    send();
    const origPush = window.history.pushState;
    window.history.pushState = function () {
      origPush.apply(window.history, arguments);
      setTimeout(send, 0);
    };
    window.addEventListener("popstate", send);
    return () => {
      window.history.pushState = origPush;
      window.removeEventListener("popstate", send);
    };
  }, [ga4Id, isProd]);

  return (
    <Helmet>
      {/* Google Search Console — meta verification (alternative to DNS TXT) */}
      {gscTag && <meta name={gscTag.split("=")[0]} content={gscTag.split("=")[1]} />}

      {/* Cloudflare Web Analytics beacon (no cookies) */}
      {isProd && cfBeacon && (
        <script defer src="https://static.cloudflareinsights.com/beacon.min.js"
                  data-cf-beacon={JSON.stringify({ token: cfBeacon })} />
      )}
    </Helmet>
  );
}
